import { useRef, useState, useEffect } from "react";
import axios from "axios";
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance";
import Spinner from "./Spinner";
import { refetchOrganization } from "../Actions/Organization/AuthActions";
import { AppDispatch, RootState } from "../store";
import { useDispatch, useSelector } from "react-redux";
import thumbsup from "../Images/thumbs_up.json"
import Lottie from "lottie-react";
import { useTranslation } from "react-i18next";
const EmailVerificationForm: React.FC = () => {
  const [otp, setOtp] = useState<string[]>(Array(6).fill("")); // Array with 4 empty strings
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]); // Array of refs for each input field
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [countdown, setCountdown] = useState<number>(600); // 60 seconds countdown
  const [isCodeExpired, setIsCodeExpired] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const isVerified =  useSelector((state: RootState) => state.auth.org?.isVerified || false) ;
  const dispatch = useDispatch<AppDispatch>()
  const {t} = useTranslation()
  const [loading, setLoading] = useState<boolean>(false);

  // Countdown logic
  useEffect(() => {
    if (isCodeSent && countdown > 0) {
      const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
      return () => clearInterval(timer);
    } else if (countdown === 0) {
      setIsCodeExpired(true);
    }
  }, [countdown, isCodeSent]);

  // Handle input change for each field
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const index = inputRefs.current.indexOf(input);
    if (index === -1) return;

    const value = input.value.trim();
    if (/^\d$/.test(value)) {
      setOtp((prevOtp) => [
        ...prevOtp.slice(0, index),
        value,
        ...prevOtp.slice(index + 1),
      ]);
      if (index < otp.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pastedText = e.clipboardData.getData("text");
    if (!/^\d+$/.test(pastedText)) {
      return; // Ensure only numeric input is accepted
    }

    const index = inputRefs.current.indexOf(e.target as HTMLInputElement);
    if (index === -1) return;

    const updatedOtp = [...otp];
    pastedText.split("").forEach((char, i) => {
      if (index + i < otp.length) {
        updatedOtp[index + i] = char;
      }
    });

    setOtp(updatedOtp);

    // Move focus to the next empty input field
    const nextIndex = Math.min(index + pastedText.length, otp.length - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const index = inputRefs.current.indexOf(input);
    if (index === -1) return;

    if (e.key === "Backspace") {
      e.preventDefault();
      setOtp((prevOtp) => [
        ...prevOtp.slice(0, index),
        "",
        ...prevOtp.slice(index + 1),
      ]);
      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };
  

  // Send verification code
  const sendCode = async () => {
    setLoading(true)
    try {
      const response = await OrganizationAxiosInstance.post("Organization/send-code");
      if (response.data?.Message === "Email verified successfully.") {
        await dispatch(refetchOrganization());
        return;
      }
      setIsCodeSent(true);
      setIsCodeExpired(false);
      setCountdown(600);
      setMessage("Verification code sent! Please check your email (and spam folder).");
    } catch (error) {
      // 429 = rate limited: show the server's "try again in X minutes" text
      setMessage(axios.isAxiosError(error) && error.response?.status === 429
        ? error.response.data?.message ?? "Too many requests. Please try again later."
        : "Failed to send code. Please try again.");
    } finally {
      setLoading(false)
    }
  };

  // Verify OTP code
  const verifyCode = async () => {
    setLoading(true)
    try {
      const code = otp.join(""); // Combine OTP digits into a single string
      const response =  await OrganizationAxiosInstance.post("Organization/verify-code/"+code);
      if(response.status==200){
        setMessage(response.data.message);
         try {
              const actionResponse = await dispatch(refetchOrganization());
              if(actionResponse.success){
                  console.log("verified")
              }else{
                  setMessage(actionResponse.message)
              }
          } catch (error) {
              console.error(error)
          }
      }
    } catch (error) {
      // 429 = too many wrong codes: the user must request a new code
      setMessage(axios.isAxiosError(error) && error.response?.status === 429
        ? error.response.data?.message ?? "Too many wrong codes. Please request a new verification code."
        : "Invalid or expired code. Please try again.");
    }finally{
        setLoading(false)
    }
  };

  return (
    <div className="flex flex-col items-center justify-center my-10  space-y-4">
      {loading? <Spinner loading />:isVerified?
      <div className="flex flex-col items-center">
        <h1 className="text-xl font-bold">{t("emailVerificationSuccess")}</h1>
        <Lottie   style={{ width: 200, height: 200 }} animationData={thumbsup} loop={true} />
      </div>
      :<><h1 className="text-xl font-bold">{t("emailVerification")}</h1>
      {/* Message */}
      {message && <p className="text-center text-sm text-red-500">{message}</p>}

      {/* OTP Inputs */}
      <div className="flex space-x-2 [direction:ltr]">
        {otp.map((digit, index) => (
          <input
            key={index}
            type="text"
            maxLength={1}
            value={digit}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onPaste={handlePaste}
            ref={(el) => (inputRefs.current[index] = el)}
            className="shadow-xs w-[64px] text-center text-2xl font-medium rounded-lg border border-stroke bg-white p-2 outline-none"
          />
        ))}
      </div>

      {/* Countdown Timer */}
      {isCodeSent && !isCodeExpired && (
        <p className="text-sm text-gray-600">
          {t("codeExpiresIn")} <span className="font-bold">{countdown}s</span>
        </p>
      )}

      {/* Buttons */}
      {!isCodeSent ? (
        <button
          onClick={sendCode}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg"
        >
         {t("sendCode")}
        </button>
      ) : isCodeExpired ? (
        <button
          onClick={sendCode}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg"
        >
          {t("resendCode")}
        </button>
      ) : (
        <button
          onClick={verifyCode}
          className="px-4 py-2 bg-green-500 text-white rounded-lg"
        >
          {t("verifyCode")}
        </button>
      )}

      {/* Hint */}
      <p className="text-sm text-gray-500">
        {t("didntRecieveCode")}
      </p></>}
    </div>
  );
};

export default EmailVerificationForm;

